import {
  TableContainer,
  Paper,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableSortLabel,
  TableBody,
} from "@mui/material";
import GenericTableProps from "@/app/interfaces/props/GenericTableProps";
import Ghost from "./ghost";

const GHOST_ITEM_COUNT = 10;

const GenericTable = <T extends Record<string, any>>({
  items,
  columns,
  sort,
  onSortChange,
  minWidth = 650,
  showHeader = true,
  noDataMessage = "No items found.",
  isLoading = false,
  isFirstColumnSticky = false,
}: GenericTableProps<T>) => {
  
  const getStickyStyles = (index: number, isHeader: boolean): any => {
    if (!isFirstColumnSticky || index !== 0) return {};
    
    return {
      position: "sticky",
      left: 0,
      zIndex: isHeader ? 3 : 2,
      backgroundColor: isHeader ? "#f9f9f9" : "white",
      boxShadow: "2px 0 5px -2px rgba(0,0,0,0.2)",
    };
  };

  return (
    <TableContainer
      component={Paper}
      elevation={0}
      sx={{
        border: "1px solid #ccc",
        borderRadius: "4px 4px 0 0",
        borderBottom: "none",
        overflowX: "auto",
        width: "100%",
        boxSizing: "border-box",
      }}
    >
      <Table size="small" sx={{ minWidth, tableLayout: "fixed" }}>
        {showHeader && (
          <TableHead sx={{ bgcolor: "#f9f9f9" }}>
            <TableRow>
              {columns.map((col, index) => (
                <TableCell
                  key={col.id}
                  align={col.align || "center"}
                  sx={{ 
                    fontWeight: "bold", 
                    width: col.width,
                    ...getStickyStyles(index, true)
                  }}
                >
                  {col.sortable !== false ? (
                    <TableSortLabel
                      active={sort.field === col.id}
                      direction={sort.field === col.id ? sort.direction : "asc"}
                      onClick={() => onSortChange(col.id)}
                    >
                      {col.label}
                    </TableSortLabel>
                  ) : (
                    col.label
                  )}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
        )}
        <TableBody>
          {isLoading ? (
            Array.from({ length: GHOST_ITEM_COUNT }).map((_, rowIndex) => (
              <TableRow key={`ghost-row-${rowIndex}`}>
                {columns.map((col, colIndex) => (
                  <TableCell
                    key={`ghost-cell-${col.id}`}
                    align={col.align || "center"}
                    sx={{ 
                      width: col.width,
                      ...getStickyStyles(colIndex, false) 
                    }}
                  >
                    <Ghost height={32} />
                  </TableCell>
                ))}
              </TableRow>
            ))
          ) : items.length > 0 ? (
            items.map((row, rowIndex) => (
              <TableRow key={rowIndex} hover>
                {columns.map((col, colIndex) => (
                  <TableCell 
                    key={col.id} 
                    align={col.align || "center"}
                    sx={{ 
                      width: col.width,
                      ...getStickyStyles(colIndex, false) 
                    }}
                  >
                    {col.render ? col.render(row, rowIndex) : row[col.id]}
                  </TableCell>
                ))}
              </TableRow>
            ))
          ) : (
            <TableRow>
              <TableCell colSpan={columns.length} align="center" sx={{ py: 3 }}>
                {noDataMessage}
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default GenericTable;
